export type SequencePodBalance = {
  id: string;
  name: string;
  balance: number;
};

const PROJECT_CARD_LAST_FOUR = '4658';

function lastFourInName(name: string): string | null {
  const match = name.match(/\b(\d{4})\b/);
  return match?.[1] || null;
}

/** QuickBooks ledger for every employee Sequence card, including Juliana's Omni. */
export function isSharedProjectCardAccount(bankAccountName: string | null | undefined): boolean {
  const n = String(bankAccountName || '').trim();
  if (!n) return false;
  const lastFour = lastFourInName(n);
  if (lastFour === PROJECT_CARD_LAST_FOUR) return true;
  // Sequence pod / unnamed ledger — not "Project Card (1111)" style per-card accounts.
  return n.toLowerCase().includes('project card') && !lastFour;
}

/** Score how likely a Sequence pod is the Project Card operating account. */
export function projectCardPodScore(name: string): number {
  const n = name.trim().toLowerCase();
  if (!n) return 0;
  if (n.includes('project card')) return 4;
  if (n.includes('project') && n.includes('card')) return 3;
  if (lastFourInName(name) === PROJECT_CARD_LAST_FOUR) return 3;
  if (/\bproject\b/.test(n) && !n.includes('short term') && !n.includes('ops')) return 1;
  return 0;
}

export function findProjectCardPod(pods: SequencePodBalance[]): SequencePodBalance | null {
  const ranked = pods
    .map(pod => ({ pod, score: projectCardPodScore(pod.name) }))
    .filter(row => row.score > 0)
    .sort((a, b) => b.score - a.score || a.pod.name.localeCompare(b.pod.name));
  return ranked[0]?.pod ?? null;
}

export function parseSequencePods(raw: unknown): SequencePodBalance[] {
  if (!Array.isArray(raw)) return [];
  const out: SequencePodBalance[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const id = typeof row.id === 'string' ? row.id.trim() : '';
    const name = typeof row.name === 'string' ? row.name.trim() : '';
    const balance = Number(row.balance);
    if (!id || !name || !Number.isFinite(balance)) continue;
    out.push({ id, name, balance });
  }
  return out;
}

export function formatFinanceDateKey(dateKey: string | null | undefined): string {
  if (!dateKey) return '';
  const [year, month, day] = dateKey.split('-').map(Number);
  if (!year || !month || !day) return dateKey;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
